/**
 * Central auth module — single source of truth for all token operations.
 *
 * Rule: POST /auth/refresh is called ONLY in two situations:
 *   1. Initial session restore on app entry (restoreSession).
 *   2. A 401 response from any API — handled by attachAuthInterceptors().
 *
 * Nothing else in the app should call the refresh endpoint.
 */
import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const AUTH_API_BASE_URL = String(import.meta.env.VITE_AUTH_API_BASE_URL || '').replace(/\/+$/, '');
const AUTH_FRONTEND_BASE_URL = String(import.meta.env.VITE_ACCOUNTS_APP_URL || '').replace(/\/+$/, '');
const AUTH_AUDIENCE = String(import.meta.env.VITE_AUTH_AUDIENCE || 'calendar-api');

const SHARED_RT_KEY = 'sculpx_rt';
const LEGACY_RT_KEYS = ['sculpx_refreshToken'] as const;
const SESSION_MARKER_KEY = 'sculpx_hasAuthSession';
const SESSION_ID_KEY = 'sculpx_sessionId';
const REDIRECT_GUARD_KEY = 'calendar_accounts_redirect_guard_ts';
const REDIRECT_GUARD_MS = 20_000;
const ANON_FAIL_GUARD_KEY = 'calendar_accounts_anon_refresh_failed_ts';
const ANON_FAIL_GUARD_MS = 30_000;

const HANDOFF_RT_PARAM = 'sculpx_rt';
const HANDOFF_CODE_PARAM = 'sculpx_hc';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AccountsSessionPayload = {
  accessToken?: string;
  user?: Record<string, unknown> | null;
  refreshToken?: string;
};

// ---------------------------------------------------------------------------
// SessionStorage helpers
// ---------------------------------------------------------------------------

export function getStoredAccessToken(): string | null {
  try {
    return sessionStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

export function persistSession(payload: AccountsSessionPayload | null | undefined): void {
  if (!payload?.accessToken) return;
  try {
    sessionStorage.setItem('accessToken', payload.accessToken);
    if (payload.user != null) {
      sessionStorage.setItem('user', JSON.stringify(payload.user));
    }
    clearRedirectGuard();
    clearAnonFailGuard();
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Guard helpers (prevent hammering /auth/refresh when clearly no session)
// ---------------------------------------------------------------------------

function setRedirectGuard(): void {
  try { sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now())); } catch { /* */ }
}

function clearRedirectGuard(): void {
  try { sessionStorage.removeItem(REDIRECT_GUARD_KEY); } catch { /* */ }
}

function hasRecentRedirectGuard(): boolean {
  try {
    const ts = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY));
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < REDIRECT_GUARD_MS;
  } catch { return false; }
}

function setAnonFailGuard(): void {
  try { sessionStorage.setItem(ANON_FAIL_GUARD_KEY, String(Date.now())); } catch { /* */ }
}

function clearAnonFailGuard(): void {
  try { sessionStorage.removeItem(ANON_FAIL_GUARD_KEY); } catch { /* */ }
}

function hasRecentAnonFail(): boolean {
  try {
    const ts = Number(sessionStorage.getItem(ANON_FAIL_GUARD_KEY));
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < ANON_FAIL_GUARD_MS;
  } catch { return false; }
}

export function shouldAutoRedirectToAccountsLogin(): boolean {
  return !hasRecentRedirectGuard();
}

// ---------------------------------------------------------------------------
// Storage cleanup
// ---------------------------------------------------------------------------

function getRootDomain(hostname: string): string | null {
  if (!hostname || hostname === 'localhost' || hostname.endsWith('.localhost')) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 2) return null;
  return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
}

function expireCookie(name: string, domain?: string): void {
  if (typeof document === 'undefined') return;
  const d = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT${d}`;
}

export function clearLegacyRefreshStorage(): void {
  try { for (const k of LEGACY_RT_KEYS) localStorage.removeItem(k); } catch { /* */ }
}

export function clearAuthStorage(): void {
  try { sessionStorage.clear(); } catch { /* */ }
  try {
    localStorage.removeItem(SHARED_RT_KEY);
    localStorage.removeItem(SESSION_MARKER_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    for (const k of LEGACY_RT_KEYS) localStorage.removeItem(k);
  } catch { /* */ }

  if (typeof window !== 'undefined') {
    const cookieNames = ['refreshToken', SESSION_MARKER_KEY, 'audience', SHARED_RT_KEY];
    cookieNames.forEach((n) => expireCookie(n));
    const root = getRootDomain(window.location.hostname);
    if (root) {
      const domain = `.${root}`;
      cookieNames.forEach((n) => expireCookie(n, domain));
    }
  }
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

function normalizeRedirectInput(raw: string): string {
  let s = raw.trim();
  const matches = [...s.matchAll(/https?:\/\//gi)];
  if (matches.length > 1 && typeof matches[1].index === 'number') {
    s = s.slice(0, matches[1].index);
  }
  const idx = s.search(/https?:\/\//i);
  if (idx > 0) s = s.slice(0, idx);
  return s.trim();
}

export function normalizeReturnRedirectUrl(pathOrUrl: string): string {
  const input = normalizeRedirectInput(pathOrUrl);
  if (!input) return `${window.location.origin}/calendar`;
  if (/^https?:\/\//i.test(input)) {
    try { return new URL(input).toString(); } catch { return `${window.location.origin}/calendar`; }
  }
  const rel = input.startsWith('/') ? input : `/${input}`;
  return `${window.location.origin}${rel}`;
}

export function getAccountsLoginUrl(redirectPathAndQuery: string): string | null {
  if (!AUTH_FRONTEND_BASE_URL) return null;
  try {
    const redirectUrl = normalizeReturnRedirectUrl(redirectPathAndQuery);
    const url = new URL(`${AUTH_FRONTEND_BASE_URL}/auth`);
    url.searchParams.set('audience', AUTH_AUDIENCE);
    url.searchParams.set('redirect', redirectUrl);
    return url.toString();
  } catch { return null; }
}

export function redirectToAccountsLogin(redirectPathAndQuery: string, opts?: { force?: boolean }): boolean {
  if (!opts?.force && hasRecentRedirectGuard()) return false;
  const url = getAccountsLoginUrl(redirectPathAndQuery);
  if (!url) return false;
  setRedirectGuard();
  window.location.replace(url);
  return true;
}

export function redirectToSignedOut(): void {
  if (typeof window !== 'undefined') window.location.replace('/auth?signed_out=1');
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

export async function logoutAccounts(accessToken: string | null): Promise<void> {
  if (!AUTH_API_BASE_URL) return;
  try {
    await axios.post(
      `${AUTH_API_BASE_URL}/auth/logout`,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }
    );
  } catch { /* server may already have revoked */ }
}

// ---------------------------------------------------------------------------
// Low-level refresh POST
// ---------------------------------------------------------------------------

function readSharedRT(): string | undefined {
  try {
    const raw = localStorage.getItem(SHARED_RT_KEY)?.trim();
    return raw || undefined;
  } catch { return undefined; }
}

function unwrapBody<T extends AccountsSessionPayload>(data: unknown): T {
  if (data && typeof data === 'object' && 'body' in data && (data as { body?: T }).body) {
    return (data as { body: T }).body;
  }
  return data as T;
}

async function postRefresh(
  oneTimeRT?: string,
  audienceOverride?: string
): Promise<AccountsSessionPayload | null> {
  if (!AUTH_API_BASE_URL) return null;
  const rt = oneTimeRT?.trim() || undefined;
  const audience = (audienceOverride ?? AUTH_AUDIENCE).trim();
  try {
    const res = await axios.post<unknown>(
      `${AUTH_API_BASE_URL}/auth/refresh`,
      { audience, ...(rt ? { refreshToken: rt } : {}) },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(rt ? { 'x-refresh-token': rt } : {}),
        },
      }
    );
    const body = unwrapBody<AccountsSessionPayload>(res.data);
    return body?.accessToken ? body : null;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      try { localStorage.removeItem(SHARED_RT_KEY); } catch { /* */ }
    }
    return null;
  }
}

// ---------------------------------------------------------------------------
// In-flight promises — declared together so refreshToken() can reference
// _restoreInFlight before the restoreSession() definition below.
// ---------------------------------------------------------------------------

let _refreshInFlight: Promise<AccountsSessionPayload | null> | null = null;
let _restoreInFlight: Promise<AccountsSessionPayload | null> | null = null;

// ---------------------------------------------------------------------------
// Primary token refresh — called ONLY from 401 interceptors
// Single-flight: concurrent callers share one in-flight request.
// If restoreSession() is still in-flight, we piggyback on it instead of
// launching a second /auth/refresh request in parallel.
// ---------------------------------------------------------------------------

export function refreshToken(): Promise<AccountsSessionPayload | null> {
  if (!AUTH_API_BASE_URL) return Promise.resolve(null);

  // If the initial restore is still running, wait for it — don't race with a
  // second /auth/refresh call. The restore result IS the refreshed token.
  if (_restoreInFlight) return _restoreInFlight;

  if (!_refreshInFlight) {
    _refreshInFlight = (async () => {
      if (!getStoredAccessToken() && !hasSessionHint()) {
        return null;
      }
      if (hasRecentAnonFail()) return null;

      const cookie = await postRefresh();
      if (cookie?.accessToken) { clearAnonFailGuard(); return cookie; }

      const sharedRT = readSharedRT();
      if (!sharedRT) { setAnonFailGuard(); return null; }

      const rt = await postRefresh(sharedRT);
      if (rt?.accessToken) { clearAnonFailGuard(); return rt; }

      setAnonFailGuard();
      return null;
    })().finally(() => { _refreshInFlight = null; });
  }

  return _refreshInFlight;
}

function hasSessionHint(): boolean {
  try {
    if (localStorage.getItem(SESSION_MARKER_KEY) === '1') return true;
    if (localStorage.getItem(SHARED_RT_KEY)?.trim()) return true;
    for (const k of LEGACY_RT_KEYS) {
      if (localStorage.getItem(k)?.trim()) return true;
    }
    return false;
  } catch { return false; }
}

// ---------------------------------------------------------------------------
// Audience-specific token cache (PMS / HRMS / Meet)
// Cached until 60 s before JWT expiry. Refreshes on cache miss or 401.
// ---------------------------------------------------------------------------

const _audienceCache = new Map<string, { token: string; exp: number }>();
const _audienceInFlight = new Map<string, Promise<AccountsSessionPayload | null>>();

function jwtExp(token: string): number | null {
  try {
    const p = JSON.parse(atob(token.split('.')[1] || '')) as { exp?: number };
    return typeof p.exp === 'number' ? p.exp : null;
  } catch { return null; }
}

export function invalidateAudienceCache(audience: string): void {
  if (audience?.trim()) _audienceCache.delete(audience.trim());
}

export function clearAllAudienceCaches(): void {
  _audienceCache.clear();
}

export function getAudienceToken(audience: string): Promise<AccountsSessionPayload | null> {
  if (!AUTH_API_BASE_URL) return Promise.resolve(null);
  const aud = audience?.trim();
  if (!aud) return Promise.resolve(null);

  const now = Math.floor(Date.now() / 1000);
  const cached = _audienceCache.get(aud);
  if (cached && cached.exp > now + 60) {
    return Promise.resolve({ accessToken: cached.token });
  }

  let p = _audienceInFlight.get(aud);
  if (!p) {
    p = (async () => {
      const cookie = await postRefresh(undefined, aud);
      if (cookie?.accessToken) return cookie;
      const sharedRT = readSharedRT();
      if (!sharedRT) return null;
      return postRefresh(sharedRT, aud);
    })().finally(() => _audienceInFlight.delete(aud));
    _audienceInFlight.set(aud, p);
  }

  return p.then((res) => {
    if (res?.accessToken) {
      const exp = jwtExp(res.accessToken);
      if (exp) _audienceCache.set(aud, { token: res.accessToken, exp });
    }
    return res;
  });
}

// ---------------------------------------------------------------------------
// URL param handoff (one-time tokens from Accounts redirect)
// ---------------------------------------------------------------------------

function readAndClearParam(param: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const qv = url.searchParams.get(param);
    if (qv) {
      url.searchParams.delete(param);
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
      return qv;
    }
    const raw = url.hash.replace(/^#/, '');
    const m = new RegExp(`(?:^|&)${param}=([^&]+)`).exec(raw);
    if (m?.[1]) {
      const decoded = decodeURIComponent(m[1]);
      const cleaned = raw.split('&').filter((p) => !p.startsWith(`${param}=`)).join('&');
      url.hash = cleaned ? `#${cleaned}` : '';
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
      return decoded;
    }
  } catch { /* */ }
  return null;
}

// ---------------------------------------------------------------------------
// Initial session restore — called ONCE on app entry, not on navigation
// ---------------------------------------------------------------------------

export function restoreSession(): Promise<AccountsSessionPayload | null> {
  if (!AUTH_API_BASE_URL) return Promise.resolve(null);
  if (!_restoreInFlight) {
    _restoreInFlight = (async () => {
      const oldToken = getStoredAccessToken();

      const handoffCode = readAndClearParam(HANDOFF_CODE_PARAM);
      if (handoffCode) {
        try {
          const { applyUserSwitchIfMismatch } = await import('./accountsUserSwitch');
          const res = await axios.post<unknown>(
            `${AUTH_API_BASE_URL}/auth/sso-handoff/redeem`,
            { audience: AUTH_AUDIENCE, code: handoffCode },
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
          );
          const body = unwrapBody<AccountsSessionPayload>(res.data);
          if (!body?.accessToken) return null;
          const incomingId = (body.user as { id?: string } | null)?.id != null
            ? String((body.user as { id?: string }).id)
            : null;
          await applyUserSwitchIfMismatch(incomingId, oldToken);
          return body;
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearLegacyRefreshStorage();
          }
          return null;
        }
      }

      const handoffRT = readAndClearParam(HANDOFF_RT_PARAM);
      if (handoffRT) {
        const { applyUserSwitchIfMismatch, decodeJwtSub } = await import('./accountsUserSwitch');
        await applyUserSwitchIfMismatch(decodeJwtSub(handoffRT), oldToken);
        const body = await postRefresh(handoffRT);
        if (body?.accessToken) return body;
      }

      // Single cookie-backed refresh on app entry — this is the only proactive refresh call.
      const cookie = await postRefresh();
      if (cookie?.accessToken) return cookie;

      if (!hasSessionHint()) return null;

      return refreshToken();
    })().finally(() => { _restoreInFlight = null; });
  }
  return _restoreInFlight;
}

// ---------------------------------------------------------------------------
// Axios interceptor factory — attach to any AxiosInstance
// Adds Bearer token from sessionStorage on every request.
// On 401: calls refreshToken() once, retries with new token.
// That is the ONLY place refresh is triggered for ongoing requests.
// ---------------------------------------------------------------------------

type RetryConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

export function attachAuthInterceptors(
  client: AxiosInstance,
  opts?: {
    /** For audience-scoped clients (PMS/HRMS/Meet): supply audience to use getAudienceToken instead of sessionStorage */
    audience?: string;
    /** Called after a failed 401 retry — use to dispatch logout in UI layer */
    onAuthFailure?: () => void;
  }
): void {
  const { audience, onAuthFailure } = opts ?? {};

  // Request: attach current token.
  // For audience-scoped clients: if the cache is empty (e.g. first request after
  // page refresh), proactively fetch the audience token now so we don't send a
  // token-less request that immediately 401s and triggers a redundant refresh round-trip.
  client.interceptors.request.use(async (config) => {
    try {
      if (audience) {
        const aud = audience.trim();
        let token = _audienceCache.get(aud)?.token;
        if (!token) {
          const session = await getAudienceToken(aud);
          token = session?.accessToken ?? undefined;
        }
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } else {
        const token = getStoredAccessToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      }
    } catch { /* */ }
    return config;
  });

  // Response: on 401, refresh once and retry — the only proactive refresh trigger
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const req = error.config as RetryConfig | undefined;
      if (error.response?.status === 401 && req && !req._authRetry) {
        req._authRetry = true;
        try {
          let session: AccountsSessionPayload | null;
          if (audience) {
            invalidateAudienceCache(audience);
            session = await getAudienceToken(audience);
          } else {
            session = await refreshToken();
            if (session?.accessToken) {
              persistSession(session);
              // Notify Redux — import lazily to avoid circular deps
              import('@/redux/store').then(({ default: store }) => {
                import('@/redux/auth/authSlice').then(({ authActions }) => {
                  store.dispatch(authActions.hydrateFromSession());
                });
              });
            }
          }
          if (session?.accessToken) {
            req.headers = req.headers ?? {};
            req.headers.Authorization = `Bearer ${session.accessToken}`;
            return client(req);
          }
        } catch { /* fall through */ }
        onAuthFailure?.();
      }
      return Promise.reject(error);
    }
  );
}
